// A bot's style: what its dials mean between drafts (AI_MANAGERS.md §4.4).
//
// TWO AXES, KEPT APART. The dials say how a bot behaves — how often it looks at
// its team, whether it offers trades, what it keeps, what it gambles on — and
// `skill` says how well it judges what it sees (`skill.ts`). A set-and-forget bot is
// not a bad one; it is one that looks less. Every number below is derived from
// one of the two and from nothing else, so a new character is new dials and
// never new code.
//
// THE FLOOR IS NOT A STYLE. Whatever the dials say, every bot benches a starter
// who cannot score when somebody on its roster can, picks up a player when
// nobody can, and answers every trade offer (§4.3). That is `planLineup`'s
// floor and `planWire`'s forced fill, which no number here turns off.

import { byBand } from './skill';

/** How a bot plays, as its seat stores it: its skill, 0 to 100, and its five dials, each 0 to 1. */
export interface BotPersona {
  skill: number;
  dials: {
    trashTalk: number;
    aggression: number;
    risk: number;
    loyalty: number;
    activity: number;
  };
}

/** What the dials mean for a season, worked out once. */
export interface BotStyle {
  /** The chance, on any day, that it looks at its team beyond what it must do. */
  lookRate: number;
  /** Whether it sets its best lineup before a kickoff, or changes only what cannot score. */
  tunesLineup: boolean;
  /** The most players it adds in a week by choice. A pickup it must make is never refused. */
  wirePerWeek: number;
  /** Weekly points a player it picks up must beat the one it drops by. */
  upgradeMargin: number;
  /** Weekly points a player it drafted is worth to it beyond his projection. */
  draftPremium: number;
  /** The most trade offers it makes in a week. Zero for a bot that never offers one. */
  offersPerWeek: number;
  /** The chance, on a day it looks, that it looks for a trade. */
  offerRate: number;
  /** Weekly points its own lineup must gain from a trade it offers. */
  tradeGain: number;
  /**
   * What a trade offered to it must gain its team, in worth over the wire and
   * its best lineup, before it says yes, as a share of the deal's size — the
   * larger side's worth (B-182, PLAN-2026-10-23 §4): its band's bar, raised
   * for a careful bot and lowered for a gambler. Below zero it takes a deal
   * short of even.
   */
  answerBar: number;
  /**
   * How far short of its bar an offer may fall for it to counter, asking for
   * one more player, rather than say no, as the same share; null for a bot
   * that never haggles.
   */
  haggle: number | null;
  /** The share of a projection it trusts from a player listed with a warning (Questionable). */
  warnTrust: number;
  /** The same, for a player listed as likely out (Doubtful). */
  likelyOutTrust: number;
  /** Whether it will pick up a player who cannot play this week, to hold him. */
  stashes: boolean;
  /** How far its reading of a projection strays, as a share of it: its skill. */
  noise: number;
  /**
   * Weekly points a player it drafted is worth to it when it sets its best
   * lineup, beyond his projection: the bottom band's sentiment, which starts
   * the guys it picked (§4.1, Lineup). Nothing above it.
   */
  sentiment: number;
  /** The most of its budget it spends on one player, as a share. */
  bidShare: number;
}

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

export function styleOf(persona: BotPersona): BotStyle {
  const activity = clamp01(persona.dials.activity);
  const aggression = clamp01(persona.dials.aggression);
  const risk = clamp01(persona.dials.risk);
  const loyalty = clamp01(persona.dials.loyalty);
  return {
    lookRate: activity,
    // A QUARTER IS THE LINE between somebody who sets a lineup and somebody
    // who lets it ride: Sunday Sam (0) lets it ride, Uncle Rico (0.3) does not.
    tunesLineup: activity >= 0.25,
    wirePerWeek: Math.round(activity * 3),
    // The busier the bot, the smaller the edge it will move for; a careful one
    // (low risk) wants a clearer upgrade than a gambler.
    upgradeMargin: Math.max(0.5, 0.5 + 3 * (1 - activity) + (0.5 - risk) * 2),
    draftPremium: loyalty * 3,
    // AT MOST TWO A WEEK, from the bots that offer most (B-49: a bot never
    // floods a league). A bot at the bottom of the dial never offers at all.
    offersPerWeek: aggression < 0.05 ? 0 : aggression >= 0.7 ? 2 : 1,
    offerRate: aggression * 0.5,
    tradeGain: byBand(persona.skill, {
      along: 0.25,
      casual: 0.5,
      solid: 1,
      sharp: 1.5,
      ruthless: 2,
    }),
    // ITS TEMPERAMENT AT THE TABLE (A4, PLAN-2026-10-23 §4), as a share of
    // the deal: the band sets how far ahead it must come out — along for the
    // ride, it takes a deal well short of even to keep a friend happy; Solid
    // wants it even; Ruthless wants to win it — and its risk moves it five
    // points of a hundred either way, a careful bot wanting more and a gambler
    // taking less. One that never offers a trade never haggles either: it
    // answers yes or no.
    answerBar:
      byBand(persona.skill, {
        along: -0.15,
        casual: -0.07,
        solid: 0,
        sharp: 0.06,
        ruthless: 0.12,
      }) +
      (0.5 - risk) * 0.1,
    haggle: aggression < 0.05 ? null : 0.05 + 0.15 * aggression,
    // A gambler trusts a Questionable star almost fully; a careful bot docks him
    // a third, enough to start a healthy player close behind him.
    warnTrust: 1 - (1 - risk) * 0.35,
    likelyOutTrust: 1 - (1 - risk) * 0.8,
    stashes: risk >= 0.7,
    noise: byBand(persona.skill, {
      along: 0.35,
      casual: 0.25,
      solid: 0.12,
      sharp: 0.04,
      ruthless: 0.01,
    }),
    sentiment: byBand(persona.skill, { along: 2, casual: 0, solid: 0, sharp: 0, ruthless: 0 }),
    bidShare: 0.05 + 0.25 * aggression,
  };
}
